import React, { useState, useEffect } from "react";

function NowPlaying({ track, audioPlayer, isPlaying, onTogglePlayPause }) {
  const [progress, setProgress] = useState(0);
  
  // Validate track data
  const isTrackValid = track && 
    track.attributes && 
    track.attributes.name && 
    track.attributes.artistName &&
    track.attributes.artwork &&
    track.attributes.artwork.url;
  
  // Update progress for the audio player
  useEffect(() => {
    if (!audioPlayer) return;
    
    const handleTimeUpdate = () => {
      const duration = audioPlayer.duration || 30; // Default to 30 seconds if duration not available
      const currentTime = audioPlayer.currentTime;
      setProgress(currentTime / duration);
    };
    
    const handleEnded = () => {
      setProgress(0);
    };
    
    // Add event listeners
    audioPlayer.addEventListener('timeupdate', handleTimeUpdate);
    audioPlayer.addEventListener('ended', handleEnded);
    
    // Clean up event listeners
    return () => {
      audioPlayer.removeEventListener('timeupdate', handleTimeUpdate);
      audioPlayer.removeEventListener('ended', handleEnded);
    };
  }, [audioPlayer]);
  
  // Sync audio player with isPlaying state
  useEffect(() => {
    if (!audioPlayer) return;
    
    if (isPlaying && audioPlayer.paused && audioPlayer.src) {
      audioPlayer.play().catch(e => console.log("Error playing:", e));
    } else if (!isPlaying && !audioPlayer.paused) {
      audioPlayer.pause();
    }
  }, [isPlaying, audioPlayer]);
  
  // For visual progress when audio player is not available
  useEffect(() => {
    if (audioPlayer) return; // Skip if real audio player is available
    
    if (!track || !isPlaying) return;
    
    let interval;
    let startTime = Date.now();
    const duration = 30000; // 30 seconds for demo purposes
    
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(elapsed / duration, 1);
      setProgress(newProgress);
      
      if (newProgress >= 1) {
        clearInterval(interval);
      }
    };
    
    interval = setInterval(updateProgress, 100);
    return () => clearInterval(interval);
  }, [track, isPlaying, audioPlayer]);
  
  // Reset progress when track changes
  useEffect(() => {
    setProgress(0);
  }, [track]);

  // Check for valid track data first
  if (!track) return null;
  
  if (!isTrackValid) {
    console.log("Invalid track data received:", track);
    
    // Use available track info even if incomplete
    return (
      <div className="now-playing">
        <div className="now-playing-info">
          <div className="now-playing-title">
            {track.attributes?.name || "Unknown Track"}
          </div>
          <div className="now-playing-artist">
            {track.attributes?.artistName || "Unknown Artist"}
          </div>
        </div>
        
        <div className="now-playing-controls">
          <button 
            className="btn-icon" 
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.15)', 
              color: 'white',
              width: '36px',
              height: '36px'
            }}
            onClick={onTogglePlayPause}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>
                <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="now-playing">
      <img 
        className="now-playing-artwork now-playing-artwork-circle" 
        src={track.attributes.artwork?.url?.replace("{w}x{h}", "96x96") || 
            track.attributes.artwork?.url?.replace("{w}", "96").replace("{h}", "96") ||
            "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'><circle cx='12' cy='12' r='10'/><path d='M12 8v4M12 16h.01'/></svg>"}
        alt={`${track.attributes.name} artwork`} 
        onError={(e) => {
          console.log("Artwork failed to load:", e.target.src);
          e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'><circle cx='12' cy='12' r='10'/><path d='M12 8v4M12 16h.01'/></svg>";
        }}
      />
      
      <div className="now-playing-info">
        <div className="now-playing-title">{track.attributes.name}</div>
        <div className="now-playing-artist">{track.attributes.artistName}</div>
        
        {/* Progress bar */}
        <div 
          style={{
            height: '3px',
            width: '100%',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '3px',
            marginTop: '4px',
            overflow: 'hidden'
          }}
        >
          <div 
            style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: 'white',
              borderRadius: '3px',
              transition: 'width 0.1s linear'
            }}
          />
        </div>
      </div>
      
      <div className="now-playing-controls">
        <button 
          className="btn-icon" 
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.15)', 
            color: 'white',
            width: '36px',
            height: '36px'
          }}
          onClick={onTogglePlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>
              <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

export default NowPlaying;